/**
 * The app's inline feedback banner.
 *
 * Exists for one reason: `role`. Around 40 sites across the pages render
 * `<span role="alert">` for request failures, and two tests assert on
 * `getByRole('alert')`. Carbon's `InlineNotification` defaults to
 * `role="status"`, which is announced politely and would silently downgrade
 * every one of those failures. This wrapper restores `alert` for errors and
 * leaves the polite role for everything else, where it is the correct one.
 *
 * Callers pass an already-formatted message — `parseApiError(err).message` —
 * the same as they do today, rather than this component reaching into error
 * shapes it cannot know about.
 */
import { InlineNotification } from '@carbon/react';

export type NotifyKind = 'error' | 'success' | 'info' | 'warning';

export interface NotifyProps {
  kind: NotifyKind;
  /** The message itself. Kept as the title, not the subtitle, so it is the announced text. */
  title: string;
  /** Optional supporting detail; announced after the title. */
  subtitle?: string;
  /** Omit to leave the banner permanent. */
  onClose?: () => void;
  className?: string;
}

export function Notify({
  kind,
  title,
  subtitle,
  onClose,
  className,
}: NotifyProps): React.JSX.Element {
  // Spread rather than pass `undefined`: this project sets
  // `exactOptionalPropertyTypes`, under which an explicit `undefined` is not
  // the same as an absent prop and Carbon's types reject it.
  return (
    <InlineNotification
      kind={kind}
      role={kind === 'error' ? 'alert' : 'status'}
      title={title}
      lowContrast
      hideCloseButton={onClose === undefined}
      {...(subtitle !== undefined && { subtitle })}
      {...(onClose !== undefined && { onClose })}
      {...(className !== undefined && { className })}
    />
  );
}

export default Notify;
